/**
 * A LISTA DE PERMISSAO DE ACESSO, que decide quem entra num ambiente que nao e producao.
 *
 * ===========================================================================
 * POR QUE ELA EXISTE, e o defeito medido que a comprou
 * ===========================================================================
 * Medido em 31/08/2026, respondendo a pergunta "como o login com Google trata conta nova
 * em hml hoje?": NAO EXISTE trigger em `auth.users`. Quem entra pelo Google ganha registro
 * no provedor e NAO ganha `public.users` nem `memberships`. Sem membership o RLS devolve
 * zero em tudo, entao a pessoa entra e nao ve dado nenhum.
 *
 * Isso parece seguro e nao e, por tres razoes:
 *
 *   1. Ela ENTRA. Ve o chassi, o menu, os rotulos das telas e a estrutura do produto.
 *      Para um ambiente que vai receber QA de fora, isso ja e acesso demais.
 *   2. NAO FICA REGISTRO de que alguem entrou. Nao ha linha em tabela nenhuma, entao nao
 *      ha como responder depois quem esteve la.
 *   3. A protecao e ACIDENTAL, e nao decidida. Ela depende de o RLS continuar exigindo
 *      membership em toda consulta. No dia em que uma tela nova ler algo sem tenant, a
 *      porta abre sem ninguem ter mudado nada de proposito.
 *
 * ===========================================================================
 * O QUE ELA NAO E, e isso importa mais que o que ela e
 * ===========================================================================
 * Ela NAO substitui o RLS, e nao e a autoridade de isolamento: essa continua sendo o banco,
 * pelo ADR 0010. Ela e a porta da RUA de um ambiente inteiro, e o RLS e a porta de cada
 * sala. Confundir as duas produz o erro classico de achar que a lista basta, e a partir dai
 * alguem autorizado passa a enxergar dado de outro cliente.
 *
 * Ela tambem NAO e o desenho final. O alvo, decidido por ele em enquete em 31/08/2026, e
 * "pessoa INATIVA no banco que um administrador ativa". Isso exige migration, trigger e
 * tela de ativacao no `apps/admin`, que hoje e chassi de espera (ADR 0030). Esta lista e a
 * trava que destrava o convite ao QA HOJE, e nao a arquitetura definitiva.
 *
 * ===========================================================================
 * A DISCIPLINA DE AMBIENTE, copiada da guarda do seed de proposito
 * ===========================================================================
 * `db/seed/guarda-ambiente.mjs` resolve o mesmo problema e resolve certo, entao o formato
 * e o mesmo: LISTA FECHADA E POSITIVA, dizendo quem PODE, e nunca quem nao pode. Escrever
 * `ambiente !== 'prd'` seria identico hoje e deixaria de ser no dia em que um quarto
 * ambiente nascer, que entraria com a porta aberta sem ninguem decidir.
 *
 * A diferenca de comportamento entre os ambientes NAO e conveniencia, e cada uma tem uma
 * razao:
 *
 *   `hml` .... a lista e OBRIGATORIA. Vazia ou ausente, NINGUEM entra. E o unico ambiente
 *              cuja URL vai para gente de fora, e um hml sem lista configurada e
 *              exatamente a porta aberta que este arquivo existe para fechar.
 *   `local` .. a lista e OPCIONAL. Vazia, todo mundo entra. Quem desenvolve nao pode ser
 *              trancado para fora da propria maquina por uma variavel esquecida, e a
 *              maquina de quem desenvolve nao esta na internet.
 *   resto .... o envelope NAO se aplica, e o provedor real segue INTOCADO. Isso alcanca
 *              `prd`, o ambiente ausente e o ambiente com grafia errada, os tres pelo mesmo
 *              motivo: em producao o controle de quem entra e o membership no banco, e um
 *              envelope pensado para homologacao nunca pode trancar producao por causa de
 *              uma variavel que ninguem lembrou de definir la.
 *
 * ===========================================================================
 * POR QUE A RECUSA ACONTECE NO `getSession`, e nao no `signInWithGoogle`
 * ===========================================================================
 * `signInWithGoogle` devolve `void` porque o fluxo SAI DA PAGINA: quem termina o login e o
 * `getSession` depois do redirecionamento de volta. Recusar dentro dele seria recusar antes
 * de existir e-mail para julgar, entao a unica recusa possivel e na volta.
 *
 * E a recusa ENCERRA A SESSAO NOS DOIS LADOS. Devolver `null` sem chamar `signOut` deixaria
 * a sessao viva no armazenamento do navegador, e a proxima carga da pagina tentaria de novo,
 * em laco, com a pessoa vendo a tela piscar sem entender. E o mesmo defeito do "sair que nao
 * sai" que o CLAUDE geral registra.
 */

import type { IdentityProvider, ResultadoLogin, Sessao } from '../identity-provider.js';

/**
 * Os ambientes em que a lista e OBRIGATORIA. Fechada e positiva, pelo motivo do cabecalho.
 *
 * So `hml` esta aqui: e o unico cuja URL sai desta casa.
 */
const AMBIENTES_COM_LISTA_OBRIGATORIA = ['hml'];

/**
 * Os ambientes em que a lista e OPCIONAL, valendo apenas quando configurada.
 *
 * So `local`, e a razao e que a maquina de quem desenvolve nao esta exposta: trancar o
 * proprio dono para fora por uma variavel esquecida custaria tempo e nao protegeria nada.
 */
const AMBIENTES_COM_LISTA_OPCIONAL = ['local'];

/**
 * A mensagem que a tela mostra na recusa, decidida por ele em enquete em 31/08/2026.
 *
 * Ela diz TRES coisas e cala uma quarta de proposito:
 *   · que a conta nao tem acesso A ESTE AMBIENTE, e nao que ela nao existe;
 *   · a quem pedir, para a pessoa ter o proximo passo;
 *   · que a sessao foi encerrada, para ela nao achar que ficou meio dentro.
 * O que ela NAO diz e se aquele e-mail existe no sistema: revelar isso entrega, a quem
 * esta testando enderecos, a informacao de quais sao validos.
 */
export const MENSAGEM_NAO_AUTORIZADO =
  'Esta conta não tem acesso a este ambiente. Peça acesso ao administrador da plataforma.';

/**
 * OS DONOS DA PLATAFORMA, e eles entram em TODO app em `local` e `hml`.
 *
 * PEDIDO DELE EM 02/09/2026: *"EM DESENV E HOMOLOGACAO O complexityds@gmail.com +
 * barrosmc@gmail.com vao entrar em TODOS OS APPS DA PLATAFORMA (pelo google) OS QUE JA
 * ESTAO AQUI E O QUE NAO ESTAO"*.
 *
 * POR QUE EM CONSTANTE, e nao na variavel de ambiente de cada app: a variavel e uma por
 * app e por ambiente, e o pedido e "TODOS OS APPS", inclusive os que ainda nao existem.
 * Confiada a variavel, a regra passaria a depender de alguem lembrar de preenche-la em
 * cada painel novo, e o app que nascesse sem ela seria justamente aquele em que ninguem
 * entra. Aqui ela nasce junto com o app, sem ninguem configurar nada.
 *
 * ELA SOMA, e nao substitui: quem preencher `CX_EMAILS_AUTORIZADOS` continua entrando
 * junto. E o caso do QA de fora, que precisa de acesso sem virar dono da plataforma.
 *
 * ISTO NAO ALCANCA PRODUCAO, e a trava e a mesma dos outros envelopes deste arquivo: em
 * `prd`, no ambiente ausente e no ambiente com grafia errada, `criarProvedorComListaDePermissao`
 * devolve o provedor real INTOCADO, e nao existe ramo de codigo em que esta lista valha la.
 *
 * EM MINUSCULAS de proposito: `lerLista` normaliza a caixa, e guardar aqui ja normalizado
 * evita que a constante e a comparacao discordem no dia em que alguem editar esta linha.
 */
export const DONOS_DA_PLATAFORMA: readonly string[] = Object.freeze([
  /**
   * UMA CONTA SO, desde 13/09/2026. Palavras dele: "o login da complexityds@gmail.com e o
   * administrador geral, esse fica (...) nao como usuario, mas como administrador UNICO",
   * e sobre a outra: "barrosmc@gmail.com, pode apagar".
   *
   * A OUTRA METADE DESTA MUDANCA E A MIGRATION 0090, que tira as mesmas contas de
   * `app.contas_de_administracao` e de `app.operadores_control_plane`. Mudar so aqui
   * deixaria o vinculo de pe no banco; mudar so la deixaria esta lista mentindo.
   */
  'complexityds@gmail.com',
]);

/**
 * O ACESSO PERMANENTE, de quem entra em TODO app em `local` e `hml` sem ser dono.
 *
 * PEDIDO DELE EM 08/09/2026: *"SEMPRE TODOS OS APPS ESSE EMAIL VAI TER ACESSO"*, logo
 * depois de pedir a liberacao painel a painel. A segunda frase e que decidiu o mecanismo:
 * "SEMPRE" e "TODOS" nao cabem numa variavel de ambiente, que e uma por app e por
 * ambiente. Preenchida a mao em quatro paineis, a regra valeria nos quatro e mentiria no
 * quinto, e o app que nascesse depois seria justamente aquele em que ele nao entra.
 *
 * POR QUE UMA CONSTANTE SEPARADA, e nao um terceiro nome dentro de `DONOS_DA_PLATAFORMA`:
 * aquele nome afirma TITULARIDADE, e quem entra por convite permanente nao e dono de
 * nada. Somar ali faria a constante mentir, e o proximo a ler usaria a lista de "donos"
 * para decidir outra coisa, com uma pessoa a mais dentro sem ninguem ter decidido isso.
 * Duas listas com nomes honestos custam uma linha na composicao e nao produzem esse erro.
 *
 * ELA SOMA, e nao substitui: quem esta na variavel de um app continua entrando junto.
 *
 * ISTO NAO ALCANCA PRODUCAO, pela mesma trava dos outros envelopes deste arquivo: em
 * `prd`, no ambiente ausente e no ambiente com grafia errada, `criarProvedorComListaDePermissao`
 * devolve o provedor real INTOCADO. Acesso permanente e a ambiente de teste, e nunca ao
 * ar de producao, onde quem decide continua sendo o membership no banco.
 *
 * E ELA NAO DA DADO NENHUM, e isso importa: esta lista e a porta da RUA do ambiente, e o
 * RLS e a porta de cada sala. Quem entra sem membership ve o chassi e zero dado.
 *
 * EM MINUSCULAS de proposito, pelo mesmo motivo da constante acima.
 */
export const ACESSO_PERMANENTE: readonly string[] = Object.freeze([
  /**
   * VAZIA desde 13/09/2026, por decisao dele: "jracaju72@gmail.com, pode apagar".
   *
   * A CONSTANTE FICA, e nao e apagada junto com o conteudo: o mecanismo que ela alimenta
   * continua valendo, e apagar a constante exigiria mexer na composicao do provedor e nos
   * casos que a provam. Vazia, ela nao soma ninguem, e volta a servir com uma linha no dia
   * em que ele quiser somar alguem de fora sem torna-la dona.
   */
]);

/**
 * Le a lista crua e devolve os e-mails normalizados, sem vazios e sem repetidos.
 *
 * A NORMALIZACAO E DE CAIXA E DE ESPACO, e so. Endereco de e-mail e insensivel a caixa na
 * parte do dominio e, na pratica, em todo provedor grande tambem na parte local: guardar
 * `QA@Empresa.com` na variavel e recusar `qa@empresa.com` no login seria um defeito que
 * ninguem consegue diagnosticar olhando a tela.
 *
 * ISTO E O OPOSTO da comparacao da conta de trabalho, que e EXATA de proposito, e a
 * diferenca e real: la o par e uma senha combinada, e alargar a porta sem necessidade seria
 * afrouxar uma credencial; aqui o valor e um endereco que uma pessoa digitou num painel,
 * e a caixa dele nao carrega significado nenhum.
 *
 * @param crua o valor bruto da variavel, com os enderecos separados por virgula
 */
export function lerLista(crua: string | undefined | null): readonly string[] {
  // Ausente, vazia ou so espaco devolve lista vazia, e quem decide o que fazer com uma
  // lista vazia e a funcao de baixo, que conhece o ambiente. Decidir aqui obrigaria esta
  // funcao a saber de ambiente, que nao e assunto dela.
  if (!crua) return [];
  return (
    crua
      // A virgula e o separador porque e o que cabe numa variavel de ambiente de uma linha
      // no painel do Render, que e onde ela vai morar de verdade.
      .split(',')
      // O trim pega o espaco depois da virgula, que todo mundo digita, e a quebra de linha
      // invisivel que vem colada quando o valor e copiado de um painel.
      .map((e) => e.trim().toLowerCase())
      // Entrada vazia acontece com virgula sobrando no fim, que e o erro de digitacao mais
      // comum numa lista. Ignorar e melhor que falhar: o resto da lista continua valendo.
      .filter((e) => e.length > 0)
      // Repetido nao muda o resultado e polui o diagnostico de quem for conferir a lista.
      .filter((e, i, todos) => todos.indexOf(e) === i)
  );
}

/**
 * Diz se um e-mail esta autorizado.
 *
 * @param email  o endereco que voltou do provedor
 * @param lista  a lista ja normalizada por `lerLista`
 */
export function estaAutorizado(
  email: string | undefined | null,
  lista: readonly string[],
): boolean {
  // Sessao sem e-mail nao pode ser julgada contra uma lista de e-mails, entao ela NAO
  // passa. E o lado seguro: um provedor que devolva sessao sem endereco nao vira um
  // caminho de entrada sem conferencia.
  if (!email) return false;
  return lista.includes(email.trim().toLowerCase());
}

/**
 * Envolve o provedor real, recusando quem nao esta na lista.
 *
 * O ENVELOPE PRESERVA O PROVEDOR REAL em vez de substitui-lo, pelo mesmo motivo registrado
 * na conta de trabalho: substituir produz o defeito classico do ambiente de teste, em que
 * o caminho usado todo dia deixa de ser o que vai para producao, e o primeiro deploy vira
 * a primeira vez que alguem exercita o codigo de verdade.
 */
function envolver(
  real: IdentityProvider,
  lista: readonly string[],
  deixarPassarParaAutocadastro = false,
): IdentityProvider {
  /**
   * Encerra a sessao nos dois lados e devolve a recusa ja pronta para a tela.
   *
   * O `signOut` e envolvido em try/catch porque a recusa NAO PODE DEPENDER de o provedor
   * responder: se a rede cair no meio, o certo continua sendo recusar, e nao deixar passar
   * porque a limpeza falhou.
   */
  async function recusar(): Promise<ResultadoLogin> {
    try {
      await real.signOut();
    } catch {
      // Silencio proposital: a limpeza e melhor-esforco, e a recusa vale de qualquer jeito.
    }
    return { ok: false, motivo: 'nao_autorizado', mensagem: MENSAGEM_NAO_AUTORIZADO };
  }

  return {
    // O nome carrega o envelope para a tela de diagnostico dizer a verdade sobre o que
    // esta no caminho. Um envelope invisivel e o que faz alguem passar uma hora
    // investigando o provedor por uma recusa que veio daqui.
    nome: `${real.nome}+lista-de-permissao`,

    inicializar: () => real.inicializar(),

    async signInWithPassword(email: string, senha: string): Promise<ResultadoLogin> {
      // A LISTA E CONFERIDA ANTES da credencial descer ao provedor. Nao e economia de
      // chamada: e nao entregar ao provedor a tentativa de login de quem nem poderia
      // entrar, o que evita disparar bloqueio por tentativa e alerta de seguranca la
      // por causa de alguem que este ambiente ja recusou.
      if (!estaAutorizado(email, lista)) {
        return { ok: false, motivo: 'nao_autorizado', mensagem: MENSAGEM_NAO_AUTORIZADO };
      }
      return real.signInWithPassword(email, senha);
    },

    // O Google segue INTOCADO aqui, e nao por esquecimento: o fluxo sai da pagina e nao ha
    // e-mail para julgar neste ponto. A recusa dele acontece no `getSession` da volta.
    signInWithGoogle: (urlRetorno: string) => real.signInWithGoogle(urlRetorno),

    signOut: () => real.signOut(),

    async getSession(): Promise<Sessao | null> {
      const sessao = await real.getSession();
      // Sem sessao nao ha o que julgar, e ausencia de login e estado normal da tela de
      // entrada, e nao erro.
      if (!sessao) return null;
      if (estaAutorizado(sessao.usuario?.email, lista)) return sessao;

      /*
       * O PORTAO DO AUTOCADASTRO, e ele vale so para quem chegou pelo GOOGLE.
       *
       * A sessao segue viva e a descoberta de vinculo decide o que ela alcanca: com vinculo,
       * o consultorio; sem vinculo, a tela de criar o proprio. Quem manda na leitura de
       * conteudo de cliente continua sendo o RLS, no banco.
       *
       * `signInWithPassword` NAO PASSA por aqui e continua recusando na porta: aquele caminho
       * e o da conta de trabalho, que e uma lista fechada de pares conhecidos, e deixar senha
       * arbitraria descer ao provedor exporia o ambiente a tentativa de forca bruta sem
       * nenhum ganho, porque conta de senha nao faz autocadastro.
       */
      if (deixarPassarParaAutocadastro) return sessao;

      // NAO AUTORIZADO: a sessao morre nos dois lados antes de devolver `null`. Sem isso
      // ela ficaria viva no armazenamento e a proxima carga tentaria de novo, em laco.
      await recusar();
      return null;
    },

    async refresh(): Promise<Sessao | null> {
      const renovada = await real.refresh();
      if (!renovada) return null;
      // A CONFERENCIA SE REPETE NA RENOVACAO, e isso nao e redundancia: a lista pode ter
      // mudado desde o login, e um acesso revogado que continua valendo ate a pessoa sair
      // sozinha nao e revogacao nenhuma.
      if (estaAutorizado(renovada.usuario?.email, lista)) return renovada;
      // O PORTAO ACOMPANHA A RENOVACAO, e sem esta linha a pessoa em autocadastro seria
      // expulsa no primeiro refresh, no meio do formulario, sem nenhuma explicacao.
      if (deixarPassarParaAutocadastro) return renovada;
      await recusar();
      return null;
    },
  } as IdentityProvider;
}

/**
 * Monta o provedor com a lista de permissao, quando o ambiente pedir.
 *
 * @param real       o provedor de verdade, que segue intocado onde a lista nao se aplica
 * @param ambiente   o valor declarado de `CX_AMBIENTE`, cru, como veio
 * @param listaCrua  o valor de `CX_EMAILS_AUTORIZADOS`, cru, separado por virgula
 */
export function criarProvedorComListaDePermissao(
  real: IdentityProvider,
  ambiente: string | undefined | null,
  listaCrua: string | undefined | null,
  /**
   * OS E-MAILS DAS CONTAS DE TRABALHO DAQUELE APP, e este parametro nasceu de um defeito
   * MEDIDO NA TELA em 08/09/2026, 22:44.
   *
   * ---------------------------------------------------------------------------
   * O DEFEITO, e ele derrubava NOVE casos de uma vez
   * ---------------------------------------------------------------------------
   * `cdsmedico@cds` e `cdssecretaria@cds` nasceram naquele dia, por pedido dele. As duas
   * eram recusadas no login com *"Esta conta nao tem acesso a este ambiente"*, e a tela
   * nunca passava da entrada. O instantaneo do Playwright registrou o alerta literal.
   *
   * A CAUSA E DE ORDEM, e nao de credencial: este envelope fica POR FORA do da conta de
   * trabalho de proposito, e `signInWithPassword` confere a lista ANTES de a credencial
   * descer. Uma conta de trabalho nova, com e-mail que ninguem pos na variavel, e recusada
   * aqui e nunca chega ao envelope que a atenderia. O sintoma acusa a CONTA quando o
   * problema e a LISTA, e `apps/medico-concierge/src/identidade.ts` ja registrava esse
   * mesmo defeito acontecido noutro produto.
   *
   * ---------------------------------------------------------------------------
   * POR QUE ELE NAO ENTRA NA DECISAO DE ENVOLVER, e esta e a parte que importa
   * ---------------------------------------------------------------------------
   * O teste de `local` continua sendo sobre `listaCrua`, e NAO sobre a lista somada. Se
   * estes e-mails contassem ali, `lista` nunca mais ficaria vazia e o envelope passaria a
   * valer em TODA maquina de desenvolvimento: quem trabalha com um e-mail que nao esta na
   * variavel ficaria trancado para fora da propria maquina, e o sintoma chegaria como "o
   * login parou de funcionar". E o mesmo raciocinio que ja vale para `DONOS_DA_PLATAFORMA`.
   *
   * ---------------------------------------------------------------------------
   * ISTO NAO ALARGA NADA, e a razao esta em outro arquivo
   * ---------------------------------------------------------------------------
   * A conta de trabalho SO EXISTE em `local` e `hml`, e quem decide isso e
   * o mesmo desenho da conta de trabalho da plataforma (que NAO existe neste repositorio desde 19/09/2026).
   * Autorizar o e-mail dela aqui nao cria caminho nenhum em `prd`: la o par nem e atendido,
   * e este envelope tambem nao se aplica. O que o parametro corrige e uma CONTRADICAO entre
   * dois mecanismos da casa, em que um concede o que o outro recusa.
   *
   * O QUE ELE NAO E: uma porta para e-mail de gente. Quem passar aqui um endereco que nao
   * seja de conta de trabalho estara criando um acesso que nao aparece em painel nenhum, e
   * a revisao disso e do fundador.
   */
  contasDeTrabalho: readonly string[] = [],
  /**
   * QUEM CHEGA PELO GOOGLE E NAO ESTA NA LISTA PASSA, para o AUTOCADASTRO decidir.
   *
   * ---------------------------------------------------------------------------
   * O DEFEITO, medido na tela pelo fundador em 13/09/2026, 17:40
   * ---------------------------------------------------------------------------
   * Ele entrou com `barrosmc@gmail.com` pelo Google, a tela "processou, nao abriu nada e
   * voltou para a tela de login". A conta havia sido removida de `DONOS_DA_PLATAFORMA`
   * naquele mesmo dia, por decisao dele, entao a recusa estava CORRETA. O comportamento e
   * que estava errado, e as palavras dele dizem qual e o certo:
   *
   *   *"como nao esta no sistema tem que entrar como se fosse um novo usuario"*.
   *
   * ---------------------------------------------------------------------------
   * O QUE ISSO REVELOU, e e maior que uma conta
   * ---------------------------------------------------------------------------
   * O produto TEM a tela de quem chega sem vinculo (`ComecarAgora`, acionada pelo estado
   * `semVinculo` de `identidade.ts:592`), e ela era INALCANCAVEL pelo Google: este envelope
   * mata a sessao no `getSession` da volta, ANTES de a descoberta de vinculo acontecer.
   *
   * Uma feature inteira existia, tinha suite propria, e nenhuma pessoa de fora conseguia
   * chegar nela pelo caminho principal. E a mesma familia do papel GESTOR que existia e que
   * ninguem conseguia vestir: parece coberto, e nao e.
   *
   * ---------------------------------------------------------------------------
   * POR QUE ISTO NAO ABRE O AMBIENTE
   * ---------------------------------------------------------------------------
   * A lista responde *"quem pode ENTRAR"*; o autocadastro responde *"quem entrou e nao
   * pertence a consultorio nenhum"*. Sao perguntas diferentes, e a segunda nao da acesso a
   * dado nenhum: sem vinculo em `memberships`, o RLS recusa toda leitura de conteudo de
   * cliente no BANCO, e nao na tela. O que a pessoa alcanca e a tela de criar o proprio
   * consultorio, que e exatamente o que o produto oferece a quem chega.
   *
   * E A TRAVA CONTRA PRODUCAO CONTINUA INTOCADA: este envelope so e aplicado em `local` e
   * `hml`; em `prd`, no ambiente ausente e no ambiente com grafia errada,
   * `criarProvedorComListaDePermissao` devolve o provedor real e nenhum destes ramos existe.
   *
   * `false` DE PADRAO, de proposito: nenhum app muda de comportamento sem alguem ter
   * decidido. O `medico-concierge` liga porque tem autocadastro; o `admin` NAO liga, porque
   * la nao existe "criar o proprio" e deixar passar seria abrir o painel de administracao.
   */
  deixarPassarParaAutocadastro = false,
): IdentityProvider {
  // TRES CONJUNTOS SOMAM, e a ordem nao importa porque `estaAutorizado` faz busca e nao
  // precedencia. O que importa e serem os tres e nao um deles: so a variavel deixaria o
  // app novo sem ninguem em hml; so os donos tiraria o caminho do QA de fora, que precisa
  // entrar sem virar dono da plataforma; e sem o acesso permanente o pedido de 08/09/2026
  // ("SEMPRE TODOS OS APPS") voltaria a depender de alguem preencher um painel por app.
  //
  // A DEDUPLICACAO E DE GRACA aqui: `lerLista` ja tira repetido, entao um dono que tambem
  // esteja escrito na variavel entra uma vez so.
  const lista = lerLista(
    [
      listaCrua,
      DONOS_DA_PLATAFORMA.join(','),
      ACESSO_PERMANENTE.join(','),
      // As contas de trabalho do app somam aqui, e NAO no teste de `local` logo abaixo. Ver
      // o bloco do parametro: contadas la, elas trancariam quem desenvolve para fora.
      contasDeTrabalho.join(','),
    ]
      .filter(Boolean)
      .join(','),
  );

  // HOMOLOGACAO: a lista vale SEMPRE, inclusive vazia. Lista vazia em hml recusa todo
  // mundo, e isso e o comportamento CERTO e nao um efeito colateral: um hml sem lista
  // configurada e a porta aberta que este arquivo existe para fechar, e falhar fechado
  // e o unico lado em que o erro custa um telefonema em vez de um vazamento.
  if (ambiente && AMBIENTES_COM_LISTA_OBRIGATORIA.includes(ambiente)) {
    return envolver(real, lista, deixarPassarParaAutocadastro);
  }

  // LOCAL: a lista so vale se ALGUEM A CONFIGUROU. O teste e sobre `listaCrua`, e nao sobre
  // `lista`, e a diferenca nasceu com os DONOS_DA_PLATAFORMA em 02/09/2026: como eles somam
  // sempre, `lista` nunca mais fica vazia, e testar por ela passaria a envolver o provedor
  // em TODA maquina de desenvolvimento. O efeito seria o oposto do pedido dele: quem
  // desenvolve com um e-mail que nao e o dos donos ficaria trancado para fora da propria
  // maquina, e o sintoma chegaria como "o login parou de funcionar".
  //
  // OS DONOS CONTINUAM ENTRANDO EM LOCAL, e por dois caminhos: sem lista configurada o
  // provedor real segue intocado e nao ha quem recusar; com lista configurada eles estao
  // dentro dela, porque somam. Nos dois casos o pedido dele vale.
  if (
    ambiente &&
    AMBIENTES_COM_LISTA_OPCIONAL.includes(ambiente) &&
    lerLista(listaCrua).length > 0
  ) {
    return envolver(real, lista, deixarPassarParaAutocadastro);
  }

  // PRODUCAO, AMBIENTE AUSENTE OU COM GRAFIA ERRADA: o provedor real segue INTOCADO.
  // Os tres caem aqui pelo mesmo motivo, e ele esta no cabecalho: em producao quem controla
  // a entrada e o membership no banco, e um envelope pensado para homologacao nunca pode
  // trancar producao por uma variavel que ninguem definiu la.
  return real;
}
